import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import {
  includesAiAlertEventWindowSelector,
  selectedAiAlertSelector,
} from '@store/trends/aiAlerts/selectors';
import { getAlertsOption, legendSeries, resetAlertOption } from './aiAlertUtils';
import {
  getInvertedPercentageMetricByKey,
  getPercentageMetricByKey,
} from '@utils/metrics/metricHelpers';
import { DEVICE_AXIS, PCP_AXIS } from './constants';
import { metricKeys } from '@pulse/metrics';
import { ecoAIAlert2AccessSelector } from '@src/store/common/selectors';
import { shouldShowImpactedUniques } from '@src/components/ai-alerts/utils';
import { useGetAIAlertType, AI_ALERT_TYPE } from '@components/ai-alerts/hooks/useGetAIAlertType';

// TODO: cleanup after depricating old trends diag alerts

// modifies input series
export function formatAlertSeries(
  option,
  alert,
  includesAlertWindow,
  showImpactedUniquesSeries,
  alertType = AI_ALERT_TYPE.SPIKE,
) {
  const { xAxis, yAxis, series } = option;

  const plotAlertSeries = !isEmpty(alert) && includesAlertWindow;
  const { metricName } = alert;
  const isPcpAlert = metricName === metricKeys.ConcurrentPlays;

  const alertSeries = plotAlertSeries
    ? getAlertsOption({ alert, xAxis, yAxis, showImpactedUniquesSeries, alertType })
    : resetAlertOption(series);

  // TODO: is anomaly label required?
  // mark main metric series as anomaly
  // const metricSeries = { id: METRIC_SERIES , name: plotAlertSeries ? 'Anomaly' : 'Primary' };

  // an axis is only rendered if there's a series tied to it
  return {
    yAxis: [
      { id: PCP_AXIS, show: plotAlertSeries && isPcpAlert },
      { id: DEVICE_AXIS, show: plotAlertSeries && !isPcpAlert },
    ],
    series: [...alertSeries],
  };
}

function useAIAlertSeries({ instance, metric, enabled = false, additionalTriggerDeps = [] }: any) {
  const alert = useSelector(selectedAiAlertSelector);
  const includesAlertWindow = useSelector(includesAiAlertEventWindowSelector);
  const ecoAIAlert2 = useSelector(ecoAIAlert2AccessSelector);
  const alertType = useGetAIAlertType(alert?.metricName);
  const isEnabled = enabled && !isEmpty(alert);

  useEffect(() => {
    if (instance && isEnabled) {
      // This metricName is the actual alert metric key, can be both percentage and normal
      const { metricName } = alert;
      // here metric name can be percentage metric as well as normal metric
      const percentageMetric = getPercentageMetricByKey({
        metricKey: metricName,
        defaultValue: metricName,
      });
      // const invertPercentageMetric = getInvertedPercentageMetricByKey({ metricKey: metricName });
      const showImpactedUniquesSeries = shouldShowImpactedUniques(ecoAIAlert2, metricName);
      // const alertMetric = invertPercentageMetric ? percentageMetric : pulse5MetricKey;

      const applyAlertOption = (alertArg) => {
        const option = instance.getOption();
        if (!option) return;
        const { yAxis, series } = formatAlertSeries(
          option,
          alertArg,
          includesAlertWindow,
          showImpactedUniquesSeries,
          alertType,
        );
        // On the reset path (alert overlay not shown), formatAlertSeries
        // returns the FULL series array with alert-related data cleared —
        // use replaceMerge: ['series'] so the internal series array is
        // fully replaced. The default merge used to leave stale bar-series
        // data (PCP Drop) on the chart, which stretched the time axis to
        // the alert window even after the user picked a datepicker range
        // outside it. The plot path (getAlertsOption) still returns a
        // subset and needs the default id-merge semantics.
        const plot = !isEmpty(alertArg) && includesAlertWindow;
        if (plot) {
          instance.setOption({ series, yAxis });
        } else {
          instance.setOption({ series, yAxis }, { replaceMerge: ['series'] });
        }
      };

      // TODO: remove timeout post additional testing
      setTimeout(() => applyAlertOption(metric === percentageMetric ? alert : {}), 100);
    }
  }, [
    instance,
    metric,
    isEnabled,
    JSON.stringify(alert),
    includesAlertWindow,
    ...additionalTriggerDeps,
  ]);

  return { legendSeries };
}

export default useAIAlertSeries;
